import type { AdminOverviewDto, MoneyByCurrency } from '@cartly/shared';
import {
  Alert,
  BarList,
  Button,
  ChartCard,
  PageHeader,
  Spinner,
  StatTile,
  TimeSeriesChart,
  cx,
} from '@cartly/ui';
import {
  Bot,
  Building2,
  CreditCard,
  Hourglass,
  MessageCircle,
  PhoneCall,
  RefreshCw,
  Repeat,
  Tag,
  UserCheck,
  Wallet,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { api } from '../../api/client';
import { formatMoney } from '../../app/format';
import { useLoader } from '../../app/hooks';

type Preset = '7d' | '30d' | '90d' | 'custom';
const PRESETS: { value: Preset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom' },
];

const dayLabel = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const keyLabel = (key: string) => dayLabel.format(new Date(`${key}T12:00:00Z`));
const today = () => new Date().toISOString().slice(0, 10);
const count = (n: number) => n.toLocaleString();

/** Relative change; null when there is nothing to compare against. */
const change = (now: number, prev: number) =>
  prev === 0 ? (now === 0 ? 0 : null) : (now - prev) / prev;

const minor = (amountMinor: number, currency: string, whole = true) =>
  formatMoney(String(amountMinor / 100), currency, { whole });

/** Currencies are listed side by side, never added together. */
function moneyLine(items: MoneyByCurrency[]): string {
  return items.length === 0 ? '—' : items.map((m) => minor(m.amountMinor, m.currency)).join(' · ');
}

function points(days: string[], values: number[]) {
  return days.map((key, i) => ({ key, value: values[i] ?? 0 }));
}

export function SuperAdminOverviewPage() {
  const [preset, setPreset] = useState<Preset>('30d');
  const [from, setFrom] = useState(() =>
    new Date(Date.now() - 29 * 86_400_000).toISOString().slice(0, 10),
  );
  const [to, setTo] = useState(today);
  const [applied, setApplied] = useState({ from, to });

  const query =
    preset === 'custom' ? `range=custom&from=${applied.from}&to=${applied.to}` : `range=${preset}`;
  const overview = useLoader(() => api.get<AdminOverviewDto>(`/admin/overview?${query}`), query);

  // Keep the previous render on screen (dimmed) while a new range loads.
  const [last, setLast] = useState<AdminOverviewDto | null>(null);
  if (overview.data && overview.data !== last) setLast(overview.data);
  const d = overview.data ?? last;

  const customInvalid = from > to || to > today();

  return (
    <div className="dashboard">
      <PageHeader
        title="Platform overview"
        description="Live figures across every merchant organization. All days are UTC."
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={() => void overview.reload()}
          >
            Refresh
          </Button>
        }
      />

      <div className="dashboard__filters" role="group" aria-label="Date range">
        <div className="segmented" role="radiogroup" aria-label="Range">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              role="radio"
              aria-checked={preset === p.value}
              className={cx('segmented__option', preset === p.value && 'segmented__option--active')}
              onClick={() => setPreset(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <>
            <label className="visually-hidden" htmlFor="sa-from">
              From
            </label>
            <input
              id="sa-from"
              type="date"
              className="input input--inline"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
            <span className="text-muted">to</span>
            <label className="visually-hidden" htmlFor="sa-to">
              To
            </label>
            <input
              id="sa-to"
              type="date"
              className="input input--inline"
              value={to}
              min={from}
              max={today()}
              onChange={(e) => setTo(e.target.value)}
            />
            <Button size="sm" disabled={customInvalid} onClick={() => setApplied({ from, to })}>
              Apply
            </Button>
          </>
        )}
        {d && (
          <span className="text-muted text-sm">
            {d.range.from} to {d.range.to} · compared with {d.range.previousFrom} to{' '}
            {d.range.previousTo}
          </span>
        )}
      </div>

      {overview.error && <Alert tone="critical">{overview.error}</Alert>}
      {!d ? (
        !overview.error && (
          <div className="page-loading">
            <Spinner size="lg" label="Loading overview" />
          </div>
        )
      ) : (
        <Content d={d} refreshing={overview.loading} />
      )}
    </div>
  );
}

function Content({ d, refreshing }: { d: AdminOverviewDto; refreshing: boolean }) {
  const { snapshot: s, period: p, series } = d;
  const days = series.days;
  const tokens = p.ai.inputTokens + p.ai.outputTokens;
  const tokenSeries = series.aiTokens.input.map((v, i) => v + (series.aiTokens.output[i] ?? 0));
  const conv = p.conversion;
  const notConverted = Math.max(0, conv.cohort - conv.converted - conv.stillTrialing);

  return (
    <div className={cx('dashboard__content', refreshing && 'dashboard__content--refreshing')}>
      <div className="kpi-grid kpi-grid--dashboard">
        <StatTile
          label="Total merchants"
          icon={<Building2 size={16} />}
          value={count(s.merchants.total)}
          hint={`${count(s.merchants.suspended)} suspended · ${count(s.merchants.closed)} closed`}
        />
        <StatTile
          label="Active merchants"
          icon={<UserCheck size={16} />}
          value={count(s.merchants.active)}
          hint="Organizations with status active"
        />
        <StatTile
          label="Trial merchants"
          icon={<Hourglass size={16} />}
          value={count(s.plans.trial)}
          hint="Running trials today"
        />
        <StatTile
          label="Free-plan merchants"
          icon={<Tag size={16} />}
          value={count(s.plans.free)}
          hint={`${count(s.plans.internal)} on the internal plan are counted separately`}
        />
        <StatTile
          label="Paid merchants"
          icon={<CreditCard size={16} />}
          value={count(s.plans.paid)}
          hint="Running paid plans"
        />
        <StatTile
          label="Active subscriptions"
          icon={<Repeat size={16} />}
          value={count(s.activeSubscriptions)}
          hint="Running trials plus paid plans"
        />
        <StatTile
          label="Monthly recurring revenue"
          icon={<Wallet size={16} />}
          value={moneyLine(s.mrr)}
          hint="List price of running paid plans, before GST; currencies are not combined"
        />
        <StatTile
          label="Payments collected"
          icon={<Wallet size={16} />}
          value={moneyLine(
            p.payments.collected.map((c) => ({ currency: c.currency, amountMinor: c.grossMinor })),
          )}
          hint={
            p.payments.collected.length === 0
              ? 'No paid payments in this range'
              : p.payments.collected
                  .map(
                    (c) => `${c.count} paid in ${c.currency}, ${minor(c.taxMinor, c.currency)} GST`,
                  )
                  .join(' · ')
          }
        />
        <StatTile
          label="WhatsApp messages sent"
          icon={<MessageCircle size={16} />}
          value={count(p.whatsapp.sent.value)}
          delta={{ change: change(p.whatsapp.sent.value, p.whatsapp.sent.previous) }}
          trend={series.whatsapp.sent}
          hint={`${count(p.whatsapp.failed)} failed · ${count(p.whatsapp.queued)} queued`}
        />
        <StatTile
          label="Automation runs"
          icon={<Zap size={16} />}
          value={count(p.automation.runs.value)}
          delta={{ change: change(p.automation.runs.value, p.automation.runs.previous) }}
          trend={series.automationRuns}
          hint={`${count(p.automation.failed)} failed`}
        />
        <StatTile
          label="AI assistant usage"
          icon={<Bot size={16} />}
          value={`${count(p.ai.runs.value)} runs`}
          delta={{ change: change(p.ai.runs.value, p.ai.runs.previous) }}
          trend={tokenSeries}
          hint={`${count(tokens)} tokens · ${count(p.ai.replies)} replies · ${count(p.ai.blocked)} blocked`}
        />
        <StatTile
          label="AI calling sessions"
          icon={<PhoneCall size={16} />}
          value={count(p.calls.sessions.value)}
          delta={{ change: change(p.calls.sessions.value, p.calls.sessions.previous) }}
          hint={`${count(p.calls.completed)} completed · ${count(p.calls.blocked)} blocked before dialling`}
        />
      </div>

      <div className="dashboard__grid">
        <ChartCard
          className="span-6"
          title="Merchant growth"
          subtitle="Organizations in total, end of each day"
          headline={`${count(s.merchants.total)} now · ${count(p.newMerchants.value)} new in range`}
          table={{
            columns: ['Date', 'Merchants'],
            rows: days.map((k, i) => [keyLabel(k), series.merchantsTotal[i] ?? 0]),
          }}
        >
          <TimeSeriesChart
            kind="area"
            data={points(days, series.merchantsTotal)}
            formatValue={(v) => `${count(v)} merchants`}
            formatKey={keyLabel}
            ariaLabel="Total merchants by day"
          />
        </ChartCard>

        <ChartCard
          className="span-6"
          title="New registrations"
          subtitle="User accounts created per day"
          headline={`${count(p.newUsers.value)} ${p.newUsers.value === 1 ? 'user' : 'users'} · ${count(p.newMerchants.value)} ${p.newMerchants.value === 1 ? 'organization' : 'organizations'}`}
          table={{
            columns: ['Date', 'Users', 'Organizations'],
            rows: days.map((k, i) => [
              keyLabel(k),
              series.newUsers[i] ?? 0,
              series.newMerchants[i] ?? 0,
            ]),
          }}
        >
          <TimeSeriesChart
            kind="column"
            data={points(days, series.newUsers)}
            formatValue={(v) => `${count(v)} new users`}
            formatKey={keyLabel}
            ariaLabel="New user registrations by day"
          />
        </ChartCard>

        <ChartCard
          className="span-4"
          title="Subscription distribution"
          subtitle="Merchants by plan, as of now"
        >
          <BarList
            items={s.byPlan.map((b) => ({
              label: b.label,
              value: b.merchants,
              display: count(b.merchants),
            }))}
            emptyMessage="No merchants yet"
          />
        </ChartCard>

        <ChartCard
          className="span-4"
          title="Trial-to-paid conversion"
          subtitle="Organizations created in this range"
          headline={
            conv.rate === null ? 'No new organizations' : `${(conv.rate * 100).toFixed(1)}%`
          }
        >
          <BarList
            items={[
              {
                label: 'Converted (paid at least once)',
                value: conv.converted,
                display: count(conv.converted),
              },
              {
                label: 'Trial still running',
                value: conv.stillTrialing,
                display: count(conv.stillTrialing),
              },
              { label: 'Not converted', value: notConverted, display: count(notConverted) },
            ]}
            emptyMessage="No new organizations in this range"
          />
        </ChartCard>

        <ChartCard
          className="span-4"
          title="AI token consumption"
          subtitle="Input and output tokens per day"
          headline={`${count(tokens)} tokens`}
          table={{
            columns: ['Date', 'Input', 'Output'],
            rows: days.map((k, i) => [
              keyLabel(k),
              series.aiTokens.input[i] ?? 0,
              series.aiTokens.output[i] ?? 0,
            ]),
          }}
        >
          <TimeSeriesChart
            kind="column"
            data={points(days, tokenSeries)}
            formatValue={(v) => `${count(v)} tokens`}
            formatKey={keyLabel}
            ariaLabel="AI tokens by day"
          />
        </ChartCard>

        {series.revenue.length === 0 ? (
          <ChartCard
            className="span-12"
            title="Revenue trend"
            subtitle="Paid payments per day, net of GST where recorded"
          >
            <div className="chart-empty">No paid payments in this range</div>
          </ChartCard>
        ) : (
          series.revenue.map((r) => {
            const total = r.netMinor.reduce((a, b) => a + b, 0);
            return (
              <ChartCard
                key={r.currency}
                className={series.revenue.length > 1 ? 'span-6' : 'span-12'}
                title={`Revenue trend (${r.currency})`}
                subtitle="Paid payments per day, net of GST where recorded"
                headline={minor(total, r.currency)}
                table={{
                  columns: ['Date', 'Net revenue'],
                  rows: days.map((k, i) => [
                    keyLabel(k),
                    minor(r.netMinor[i] ?? 0, r.currency, false),
                  ]),
                }}
              >
                <TimeSeriesChart
                  kind="column"
                  data={points(
                    days,
                    r.netMinor.map((v) => v / 100),
                  )}
                  formatValue={(v) => formatMoney(String(v), r.currency)}
                  formatKey={keyLabel}
                  ariaLabel={`Net revenue in ${r.currency} by day`}
                />
              </ChartCard>
            );
          })
        )}

        <ChartCard
          className="span-6"
          title="WhatsApp usage"
          subtitle="Outbound messages accepted by Meta, per day"
          headline={`${count(p.whatsapp.sent.value)} sent · ${count(p.whatsapp.failed)} failed`}
          table={{
            columns: ['Date', 'Sent', 'Failed'],
            rows: days.map((k, i) => [
              keyLabel(k),
              series.whatsapp.sent[i] ?? 0,
              series.whatsapp.failed[i] ?? 0,
            ]),
          }}
        >
          <TimeSeriesChart
            kind="column"
            data={points(days, series.whatsapp.sent)}
            formatValue={(v) => `${count(v)} sent`}
            formatKey={keyLabel}
            ariaLabel="WhatsApp messages sent by day"
          />
        </ChartCard>

        <ChartCard
          className="span-6"
          title="Automation usage"
          subtitle="Flow runs started per day"
          headline={`${count(p.automation.runs.value)} runs`}
          table={{
            columns: ['Date', 'Runs'],
            rows: days.map((k, i) => [keyLabel(k), series.automationRuns[i] ?? 0]),
          }}
        >
          <TimeSeriesChart
            kind="column"
            data={points(days, series.automationRuns)}
            formatValue={(v) => `${count(v)} runs`}
            formatKey={keyLabel}
            ariaLabel="Automation runs by day"
          />
        </ChartCard>
      </div>

      <details className="definitions">
        <summary>How these figures are calculated</summary>
        <dl>
          {Object.entries(d.definitions).map(([key, text]) => (
            <div key={key}>
              <dt>{key}</dt>
              <dd>{text}</dd>
            </div>
          ))}
        </dl>
      </details>
    </div>
  );
}
